import { API_BASE_URL } from "@constants/urls";
import authAxios from "@helpers/lib/axios";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import type { BackendSessionResponse } from "@memorang/types";
import type {
	CreateSessionRequest,
	EndSessionRequest,
	EndSessionResponse,
	TrackAnswerEventRequest,
	TrackAnswerEventResponse,
} from "./types";

type UpdateSessionBreakRequest = {
	sessionId: number;
	config: {
		allotedTime: number;
		section: string;
		startTime: string;
		status: string;
		totalBreakTimeTakenInSeconds: number;
	}[];
};

export const updateSessionBreak = async (
	payload: UpdateSessionBreakRequest,
): Promise<void> => {
	const { data } = await authAxios.post(
		`${API_BASE_URL}/assessments/v1/responses/break-events`,
		payload,
	);

	return data.data;
};

export async function createSession(
	request: CreateSessionRequest,
): Promise<BackendSessionResponse> {
	const { data } = await authAxios.post(
		`${API_BASE_URL}/assessments/v1/sessions`,
		request,
		{
			headers: {
				"Content-Type": "application/json",
			},
		},
	);

	return data.data;
}

export async function endSession(
	request: EndSessionRequest,
): Promise<EndSessionResponse> {
	const { data } = await authAxios.post(
		`${API_BASE_URL}/assessments/v1/sessions/${request.sessionId}/end`,
	);
	return data.data;
}

export async function trackAnswerEvent(
	request: TrackAnswerEventRequest,
): Promise<TrackAnswerEventResponse> {
	try {
		const { data } = await authAxios.post(
			`${API_BASE_URL}/assessments/v1/responses/answer-events`,
			request,
			{
				headers: {
					"Content-Type": "application/json",
				},
			},
		);

		return data.data;
	} catch (_error) {
		const error = new Error("Error tracking answer event");
		const axiosError = _error as {
			response?: { status?: number; data?: unknown };
			code?: string;
			message?: string;
		};
		captureErrorToSentry(error, {
			error: serializeErrorForSentry(_error),
			request: serializeErrorForSentry(request),
			responseStatus: axiosError?.response?.status,
			responseData: serializeErrorForSentry(axiosError?.response?.data),
			errorCode: axiosError?.code,
			errorMessage: axiosError?.message,
		});
		throw _error;
	}
}
export const fetchSessionItems = async ({
	sessionId,
}: {
	sessionId: number;
}): Promise<BackendSessionResponse> => {
	const { data } = await authAxios.get(
		`${API_BASE_URL}/assessments/v1/sessions/${sessionId}/items`,
	);
	return data.data;
};
